import api from './api';
import localDB from './localDB';
import networkService from './networkService';
import notificationService from './notificationService';
import { createOperationId } from '../utils/operationId';
import { normalizeDueDateForSubmit } from '../utils/normalizeDueDateForSubmit';
import { normalizeCostForSubmit } from '../utils/normalizeCostForSubmit';
import { isOfflineQueueableError } from '../utils/offlineError';
import { withOptionalRequestData } from '../utils/requestConfig';
import { normalizeTaskKindPayload } from '../utils/taskKind';

function normalizeTaskPayload(data) {
  const payload = normalizeTaskKindPayload(data);
  normalizeDueDateForSubmit(payload);
  normalizeCostForSubmit(payload);
  if (Object.prototype.hasOwnProperty.call(payload, 'due_date') && payload.due_date === undefined) {
    payload.due_date = null;
  }
  return payload;
}

function isTemporaryTaskId(id) {
  const value = String(id ?? '');
  return value.startsWith('offline_') || value.startsWith('temp_');
}

async function refreshNotifications() {
  try {
    await notificationService.refreshTask();
  } catch (error) {
    console.warn('Task notification refresh failed:', error);
  }
}

async function enqueueTaskOperation({
  operationId,
  method,
  url,
  data = null,
  entityLocalId,
  afterActions = [],
}) {
  await localDB.addToSyncQueue(
    method.toUpperCase(),
    url,
    data,
    { 'X-Operation-Id': operationId },
    10,
    {
      operation_id: operationId,
      entity_type: 'task',
      entity_local_id: String(entityLocalId ?? ''),
      after_actions: afterActions,
    }
  );
}

async function queueCreate(payload, operationId) {
  const localTask = await localDB.addPendingTask(payload, operationId);
  await enqueueTaskOperation({
    operationId,
    method: 'POST',
    url: '/tasks',
    data: { ...payload, created_at: localTask.created_at },
    entityLocalId: localTask.temp_id,
  });
  await refreshNotifications();
  return localTask;
}

async function updatePendingCreate(tempId, updates, afterAction = null) {
  const queueItem = await localDB.getQueueItemByLocalEntity('task', String(tempId));
  if (!queueItem) {
    throw new Error('Локальная операция создания задачи не найдена');
  }

  const nextData = updates ? { ...(queueItem.data || {}), ...updates } : queueItem.data;
  const nextActions = Array.isArray(queueItem.after_actions) ? [...queueItem.after_actions] : [];
  if (afterAction) {
    const sameType = nextActions.findIndex((item) => item.type === afterAction.type);
    if (sameType >= 0) nextActions[sameType] = afterAction;
    else nextActions.push(afterAction);
  }

  await localDB.updateSyncQueueItem(queueItem.id, {
    data: nextData,
    after_actions: nextActions,
    updated_at: new Date().toISOString(),
    status: 'pending',
    next_retry_at: null,
    error: null,
  });
}

async function requestOrQueue({
  operationId = createOperationId(),
  method,
  url,
  data = null,
  entityLocalId,
  optimistic,
}) {
  const config = withOptionalRequestData({
    method,
    url,
    headers: { 'X-Operation-Id': operationId },
    _offlineHandled: true,
  }, data);

  const online = await networkService.isConnected();
  if (online) {
    try {
      return await api.request(config);
    } catch (error) {
      if (!isOfflineQueueableError(error)) throw error;
    }
  }

  await enqueueTaskOperation({
    operationId,
    method,
    url,
    data,
    entityLocalId,
  });
  const localResult = await optimistic();
  await refreshNotifications();
  return {
    data: localResult,
    offline: true,
    queuedForSync: true,
  };
}

export const tasksService = {
  getAll: async (params = {}) => {
    const response = await api.get('/tasks', { params });
    if (response && response.data) {
      return Array.isArray(response.data) ? { data: response.data } : response.data;
    }
    if (Array.isArray(response)) return { data: response };
    return response || { data: [] };
  },

  getCompletedInRange: async (startDate, endDate) => {
    const start = startDate ? new Date(startDate).toISOString().slice(0, 10) : null;
    const end = endDate ? new Date(endDate).toISOString().slice(0, 10) : null;
    const params = { status: 'completed' };
    if (start) params.created_after = start;
    if (end) params.created_before = end;
    const response = await api.get('/tasks', { params });
    const data = response?.data;
    const raw = Array.isArray(data) ? data : (data?.data ?? []);
    return (raw || [])
      .filter((task) => task && (task.id != null || task.title) && task.status === 'completed')
      .map((task) => {
        const reportDate = task.due_date || task.created_at;
        const displayDate = task.report_display_date
          || (reportDate ? new Date(reportDate).toISOString().slice(0, 10) : null);
        return {
          ...task,
          report_date: task.report_date ?? reportDate,
          report_display_date: displayDate,
        };
      });
  },

  getIncomplete: async () => {
    const response = await api.get('/tasks/incomplete');
    if (response && response.data) {
      return Array.isArray(response.data) ? { data: response.data } : response.data;
    }
    if (Array.isArray(response)) return { data: response };
    return response || { data: [] };
  },

  getLocalTasks: async () => {
    try {
      return await localDB.getTasks();
    } catch (error) {
      return [];
    }
  },

  getById: async (id) => {
    if (isTemporaryTaskId(id)) {
      return localDB.getTask(id);
    }
    try {
      const response = await api.get(`/tasks/${id}`);
      return response?.data ?? null;
    } catch (error) {
      const local = await localDB.getTask(id);
      if (local) return local;
      throw error;
    }
  },

  create: async (data) => {
    const payload = normalizeTaskPayload(data);
    const operationId = createOperationId();
    const online = await networkService.isConnected();

    if (online) {
      try {
        const response = await api.post('/tasks', payload, {
          headers: { 'X-Operation-Id': operationId },
          _offlineHandled: true,
        });
        await localDB.addTask(response.data, false);
        await refreshNotifications();
        return response.data;
      } catch (error) {
        if (!isOfflineQueueableError(error)) throw error;
      }
    }

    return queueCreate(payload, operationId);
  },

  update: async (id, data) => {
    const payload = normalizeTaskPayload(data);
    if (isTemporaryTaskId(id)) {
      await updatePendingCreate(id, payload);
      await localDB.updateTask(id, payload, true);
      const local = await localDB.getTask(id);
      await refreshNotifications();
      return local;
    }

    const response = await requestOrQueue({
      method: 'PUT',
      url: `/tasks/${id}`,
      data: payload,
      entityLocalId: id,
      optimistic: async () => {
        await localDB.updateTask(id, payload, true);
        return localDB.getTask(id);
      },
    });
    if (!response.offline && response.data) {
      await localDB.addTask(response.data, false);
      await refreshNotifications();
    }
    return response.data;
  },

  markPaid: async (id, date = null) => {
    if (isTemporaryTaskId(id)) {
      throw new Error('Сначала синхронизируйте задачу');
    }
    const payload = date ? { date } : {};
    const operationId = createOperationId();
    const response = await api.post(`/tasks/${id}/pay`, payload, {
      headers: { 'X-Operation-Id': operationId },
      _offlineHandled: true,
    });
    if (response && response.data) return { data: response.data };
    return { data: response || {} };
  },

  complete: async (id) => {
    const completedAt = new Date().toISOString();
    if (isTemporaryTaskId(id)) {
      await updatePendingCreate(id, null, {
        type: 'complete',
        operation_id: createOperationId(),
      });
      await localDB.updateTask(id, { status: 'completed', completed_at: completedAt }, true);
      const task = await localDB.getTask(id);
      await refreshNotifications();
      const localBody = { message: 'Задача выполнена и ждёт синхронизации', task, amount_due: Number(task?.cost || 0) };
      window.dispatchEvent(new CustomEvent('crm:task-completed', { detail: { task, taskId: id } }));
      return { data: localBody, offline: true, queuedForSync: true };
    }

    const response = await requestOrQueue({
      method: 'POST',
      url: `/tasks/${id}/complete`,
      entityLocalId: id,
      optimistic: async () => {
        await localDB.updateTask(id, { status: 'completed', completed_at: completedAt }, true);
        const task = await localDB.getTask(id);
        return { message: 'Задача выполнена и ждёт синхронизации', task, amount_due: Number(task?.cost || 0) };
      },
    });
    const body = response?.data || response || {};
    if (!response.offline && body.task) await localDB.addTask(body.task, false);
    await refreshNotifications();
    window.dispatchEvent(new CustomEvent('crm:task-completed', { detail: { task: body.task, taskId: id } }));
    return { data: body };
  },

  reopen: async (id) => {
    if (isTemporaryTaskId(id)) {
      await updatePendingCreate(id, { status: 'in_progress', completed_at: null });
      await localDB.updateTask(id, { status: 'in_progress', completed_at: null }, true);
      const task = await localDB.getTask(id);
      await refreshNotifications();
      return { data: { message: 'Задача возвращена в работу', task } };
    }

    const response = await requestOrQueue({
      method: 'POST',
      url: `/tasks/${id}/reopen`,
      entityLocalId: id,
      optimistic: async () => {
        await localDB.updateTask(id, { status: 'in_progress', completed_at: null }, true);
        return { message: 'Задача возвращена в работу и ждёт синхронизации', task: await localDB.getTask(id) };
      },
    });
    const body = response?.data || response || {};
    if (!response.offline && body.task) await localDB.addTask(body.task, false);
    await refreshNotifications();
    return { data: body };
  },

  wait: async (id, data = {}) => {
    if (isTemporaryTaskId(id)) {
      await updatePendingCreate(id, null, {
        type: 'wait',
        data,
        operation_id: createOperationId(),
      });
      await localDB.updateTask(id, {
        status: 'waiting',
        waiting_reason: data.reason || null,
        waiting_comment: data.comment || null,
        waiting_until: data.remind_at || null,
      }, true);
      return { message: 'Задача переведена в ожидание', task: await localDB.getTask(id) };
    }
    const response = await requestOrQueue({
      method: 'POST',
      url: `/tasks/${id}/wait`,
      data,
      entityLocalId: id,
      optimistic: async () => {
        await localDB.updateTask(id, {
          status: 'waiting',
          waiting_reason: data.reason || null,
          waiting_comment: data.comment || null,
          waiting_until: data.remind_at || null,
        }, true);
        return { message: 'Задача переведена в ожидание и ждёт синхронизации', task: await localDB.getTask(id) };
      },
    });
    return response?.data || response || {};
  },

  resume: async (id) => {
    if (isTemporaryTaskId(id)) {
      await updatePendingCreate(id, { status: 'in_progress' });
      await localDB.updateTask(id, {
        status: 'in_progress',
        waiting_reason: null,
        waiting_comment: null,
        waiting_until: null,
      }, true);
      return { message: 'Задача возвращена в работу', task: await localDB.getTask(id) };
    }
    const response = await requestOrQueue({
      method: 'POST',
      url: `/tasks/${id}/resume`,
      entityLocalId: id,
      optimistic: async () => {
        await localDB.updateTask(id, {
          status: 'in_progress',
          waiting_reason: null,
          waiting_comment: null,
          waiting_until: null,
        }, true);
        return { message: 'Задача возвращена в работу и ждёт синхронизации', task: await localDB.getTask(id) };
      },
    });
    return response?.data || response || {};
  },

  addHistory: async (id, comment) => {
    if (isTemporaryTaskId(id)) {
      await updatePendingCreate(id, null, {
        type: 'history',
        data: { comment },
        operation_id: createOperationId(),
      });
      return { id: `offline_history_${Date.now()}`, comment, created_at: new Date().toISOString() };
    }
    const response = await requestOrQueue({
      method: 'POST',
      url: `/tasks/${id}/history`,
      data: { comment },
      entityLocalId: id,
      optimistic: async () => ({ id: `offline_history_${Date.now()}`, comment, created_at: new Date().toISOString() }),
    });
    return response?.data || response || {};
  },

  delete: async (id) => {
    if (isTemporaryTaskId(id)) {
      const createItem = await localDB.getQueueItemByLocalEntity('task', String(id));
      if (createItem) await localDB.removeFromSyncQueue(createItem.id);
      await localDB.deleteTask(id);
      await refreshNotifications();
      return { id, deleted: true, offline: true };
    }

    const response = await requestOrQueue({
      method: 'DELETE',
      url: `/tasks/${id}`,
      entityLocalId: id,
      optimistic: async () => {
        await localDB.deleteTask(id);
        return { id, deleted: true };
      },
    });
    if (!response.offline) {
      await localDB.deleteTask(id).catch(() => {});
    }
    await refreshNotifications();
    return response.data;
  },
};
